/**
 * WallSketch Snap Engine
 *
 * Inference-driven snap resolver for the skeleton editor. The caller passes
 * a list of *construction lines* (inferred from adjacent geometry — parallel,
 * perpendicular, collinear, etc.) plus a tight list of existing nodes for
 * "land exactly on this node" snaps. The engine returns either:
 *
 *  - `node`        — the candidate is within nodeRadius of an existing node
 *  - `intersection`— two construction lines cross near the candidate
 *  - `line`        — the candidate is near a single construction line
 *  - `free`        — no snap, returns candidate unchanged
 *
 * Coordinate-space agnostic: caller passes everything in whichever 2D space
 * they choose (the session uses a relative space anchored at a reference
 * point so float math stays close to (0, 0) even at UTM-coord scale).
 *
 * Modifier semantics:
 *  - `default` → tries nodes, then construction-line intersections, then single lines.
 *  - `strict`  → only tries existing-node snap (Shift = "exact placement only").
 *  - `none`    → no snapping (Ctrl = free movement).
 */

import * as THREE from 'three';
import { SkeletonEdgeData } from '../../../utils/PointoramaWallSketch';
import type { PointoramaWallSketch } from '../../../utils/PointoramaWallSketch';
import type { PixelHitTester } from '../modules/selection/PixelHitTester';
import { closestPointOnEdge, edgeLength as arcEdgeLength, lineArcIntersections, edgeIsCurved } from '../../../utils/skeleton/arcGeometry';

export type SnapModifier = 'default' | 'strict' | 'none';

export type SnapKind = 'node' | 'edge-cross' | 'on-edge' | 'intersection' | 'line' | 'free';

/** Why a construction line exists — drives color + the visual link to source geometry. */
export type ConstructionKind = 'parallel' | 'perpendicular' | 'axis';

/**
 * A construction line inferred from adjacent geometry. Infinite in both
 * directions. Carries structured metadata so the visual layer can color the
 * guide by kind and highlight / draw a connector to the source geometry.
 */
export interface ConstructionLine {
  /** Anchor through which the line passes (in caller's coord space). */
  origin: { x: number; y: number };
  /** Unit direction vector. */
  direction: { x: number; y: number };
  kind: ConstructionKind;
  /**
   * Source node this inference draws from (e.g. neighbour through which a
   * perpendicular passes). Used to highlight the node and to anchor the
   * source→snap connector. Either this or `sourceEdgeId` is typically set.
   */
  sourceNodeId?: string;
  /** Source edge this inference draws from. Same purpose as `sourceNodeId`. */
  sourceEdgeId?: string;
  /**
   * Midpoint of the source geometry (edge midpoint, or node position) in
   * caller's coord space. Used by bucketing as the "this snap belongs
   * here" location when ranking same-bucket reps by distance to an
   * inception point — avoids a degenerate tie when multiple lines share
   * the same `origin` (e.g. all extrude-from-edge lines anchored at the
   * split point). Optional; if omitted, bucketing falls back to `origin`.
   */
  sourceMidpoint?: { x: number; y: number };
}

export interface SnapResult {
  /** Adjusted point in the same coord space the caller passed in. */
  point: THREE.Vector2;
  kind: SnapKind;
  /** Node id when kind === 'node'. */
  nodeId?: string;
  /** Edge id when kind === 'edge-cross' — caller is expected to split this edge at `point`. */
  edgeId?: string;
  /** Up to two construction lines responsible for the snap (for visualisation). */
  lines?: ConstructionLine[];
}

export interface SnapNode {
  id: string;
  position: { x: number; y: number };
}

export interface SnapEdge {
  id: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
  /** Arc curvature of the edge (0/absent = straight). Edge-cross and on-edge
   * snaps resolve against the arc when set. */
  bulge?: number;
}

export interface SnapContext {
  /** Existing nodes the candidate can snap to (tight radius). */
  nodes: SnapNode[];
  /**
   * Existing edge segments. Used for the edge-cross snap kind: where a
   * construction line crosses an edge, the candidate snaps onto that crossing
   * and the caller is expected to split the edge at that point. Pass empty
   * to disable edge-cross snaps.
   */
  edges: SnapEdge[];
  /** Construction lines inferred from adjacent geometry. */
  constructionLines: ConstructionLine[];
  candidate: THREE.Vector2;
  modifier: SnapModifier;
  ignoreNodeIds?: Set<string>;
  /** Edges to skip in edge-cross snap (e.g. edges incident to a moving node). */
  ignoreEdgeIds?: Set<string>;
  /** Tight node-snap radius (small — "land exactly on this node"). */
  nodeRadius: number;
  /** How close the candidate must be to a construction line to engage. */
  lineTolerance: number;
  /**
   * Opt-in: also snap directly to the closest point on any edge segment
   * within `lineTolerance` of the candidate, **even when no construction
   * line is engaged**. Used by the manual-width tool so the user can drop
   * onto a room wall regardless of whether it happens to coincide with the
   * source edge's parallel/perpendicular construction lines. Off by default
   * because other drag modes (move-node, extrude) intentionally avoid
   * noisy edge-proximity snaps.
   */
  allowDirectEdgeSnap?: boolean;
}

export class WallSketchSnapEngine {
  resolve(ctx: SnapContext): SnapResult {
    const candidate = ctx.candidate;

    if (ctx.modifier === 'none') {
      return { point: candidate.clone(), kind: 'free' };
    }

    // 1. Existing node — highest priority, very tight radius.
    const nodeHit = this.snapToNode(ctx, candidate);
    if (nodeHit) {
      return {
        point: new THREE.Vector2(nodeHit.position.x, nodeHit.position.y),
        kind: 'node',
        nodeId: nodeHit.id,
      };
    }

    // strict (Shift) stops here — no inferred snaps.
    if (ctx.modifier === 'strict') {
      return { point: candidate.clone(), kind: 'free' };
    }

    // 2. Find every construction line whose distance from the candidate is
    // within tolerance. We sort by distance so closest-first.
    //
    // The caller is responsible for deduping near-coincident lines via
    // `bucketConstructionLines` BEFORE passing them in — typically once at
    // drag start, since the lines depend only on skeleton topology + drag
    // context (both static during a drag). Bucketing the live `close` list
    // per frame would let lines pop in/out of buckets as they cross the
    // tolerance boundary, which produces visible twitch even with stable
    // representatives. Doing it once on the full set, against no candidate,
    // gives bucket membership that doesn't change frame-to-frame.
    const close: { line: ConstructionLine; dist: number; projected: THREE.Vector2 }[] = [];
    for (const line of ctx.constructionLines) {
      const projected = projectPointOntoLine(candidate, line);
      const dist = projected.distanceTo(candidate);
      if (dist < ctx.lineTolerance) {
        close.push({ line, dist, projected });
      }
    }
    close.sort((a, b) => a.dist - b.dist);

    // 3. Edge-cross — where a close construction line intersects an existing
    // edge segment. Higher priority than free-space line/intersection snaps:
    // attaching to existing geometry is what the user most wants and avoids
    // visually-adjacent-but-disconnected nodes.
    const edgeCross = this.findEdgeCross(ctx, close);
    if (edgeCross) {
      return {
        point: edgeCross.point,
        kind: 'edge-cross',
        edgeId: edgeCross.edgeId,
        lines: [edgeCross.line],
      };
    }

    // 3b. On-edge (manual-width opt-in) — closest point on any edge segment
    // within tolerance, regardless of whether a construction line is engaged.
    // The edge-cross step above already covers crossings where a construction
    // line is involved; this catches the "I'm hovering near a wall that
    // doesn't line up with any construction line" case.
    if (ctx.allowDirectEdgeSnap) {
      const onEdge = this.findClosestEdgePoint(ctx);
      if (onEdge) {
        return {
          point: onEdge.point,
          kind: 'on-edge',
          edgeId: onEdge.edgeId,
        };
      }
    }

    // 4. Intersection of the two closest lines, if both engaged AND their
    // intersection sits near the candidate. This is how AutoCAD-style polar
    // tracking surfaces "snap to where 90° from A meets parallel-to-B."
    if (close.length >= 2) {
      const inter = lineLineIntersection(close[0].line, close[1].line);
      if (inter && inter.distanceTo(candidate) < ctx.lineTolerance * 1.5) {
        return {
          point: inter,
          kind: 'intersection',
          lines: [close[0].line, close[1].line],
        };
      }
    }

    // 5. Single construction line.
    if (close.length >= 1) {
      return {
        point: close[0].projected,
        kind: 'line',
        lines: [close[0].line],
      };
    }

    return { point: candidate.clone(), kind: 'free' };
  }

  /**
   * Find the closest "construction line crosses an existing edge" hit, if any.
   * The crossing must:
   *  - Lie within the edge segment (not on its extension).
   *  - Lie within `lineTolerance` of the candidate (so the user is actually
   *    aiming there, not just any random crossing in the scene).
   * Returns the closest such hit, or null.
   */
  private findEdgeCross(ctx: SnapContext, closeLines: { line: ConstructionLine; dist: number; projected: THREE.Vector2 }[]): { point: THREE.Vector2; edgeId: string; line: ConstructionLine } | null {
    if (closeLines.length === 0 || ctx.edges.length === 0) return null;
    let best: { point: THREE.Vector2; edgeId: string; line: ConstructionLine; dist: number } | null = null;
    for (const { line } of closeLines) {
      for (const edge of ctx.edges) {
        if (ctx.ignoreEdgeIds?.has(edge.id)) continue;
        if (edgeIsCurved(edge)) {
          // Line × arc — up to two hits within the arc's span.
          for (const p of lineArcIntersections(line.origin, line.direction, edge.start, edge.end, edge.bulge)) {
            const hit = new THREE.Vector2(p.x, p.y);
            const dist = hit.distanceTo(ctx.candidate);
            if (dist >= ctx.lineTolerance * 1.5) continue;
            if (!best || dist < best.dist) {
              best = { point: hit, edgeId: edge.id, line, dist };
            }
          }
          continue;
        }
        const hit = lineSegmentIntersection(line, edge.start, edge.end);
        if (!hit) continue;
        const dist = hit.distanceTo(ctx.candidate);
        if (dist >= ctx.lineTolerance * 1.5) continue;
        if (!best || dist < best.dist) {
          best = { point: hit, edgeId: edge.id, line, dist };
        }
      }
    }
    return best ? { point: best.point, edgeId: best.edgeId, line: best.line } : null;
  }

  /**
   * Closest point on any edge segment to the candidate, if it lies within
   * `lineTolerance`. Independent of construction lines — used by tools that
   * want to snap directly to wall geometry (manual width measurement).
   * Returns the closest match across all eligible edges, or null.
   */
  private findClosestEdgePoint(ctx: SnapContext): { point: THREE.Vector2; edgeId: string } | null {
    if (ctx.edges.length === 0) return null;
    let best: { point: THREE.Vector2; edgeId: string; dist: number } | null = null;
    for (const edge of ctx.edges) {
      if (ctx.ignoreEdgeIds?.has(edge.id)) continue;
      const foot = closestPointOnEdge({ x: ctx.candidate.x, y: ctx.candidate.y }, edge.start, edge.end, edge.bulge);
      const dist = foot.distance;
      if (dist >= ctx.lineTolerance) continue;
      if (!best || dist < best.dist) {
        best = { point: new THREE.Vector2(foot.point.x, foot.point.y), edgeId: edge.id, dist };
      }
    }
    return best ? { point: best.point, edgeId: best.edgeId } : null;
  }

  private snapToNode(ctx: SnapContext, candidate2: THREE.Vector2): SnapNode | null {
    let best: SnapNode | null = null;
    let bestDist = ctx.nodeRadius;
    for (const node of ctx.nodes) {
      if (ctx.ignoreNodeIds?.has(node.id)) continue;
      const dx = node.position.x - candidate2.x;
      const dy = node.position.y - candidate2.y;
      const d = Math.hypot(dx, dy);
      if (d < bestDist) {
        bestDist = d;
        best = node;
      }
    }
    return best;
  }
}

/** Project a point onto an infinite line. */
function projectPointOntoLine(p: THREE.Vector2, line: ConstructionLine): THREE.Vector2 {
  const ox = p.x - line.origin.x;
  const oy = p.y - line.origin.y;
  const t = ox * line.direction.x + oy * line.direction.y;
  return new THREE.Vector2(line.origin.x + line.direction.x * t, line.origin.y + line.direction.y * t);
}

/**
 * Collapse near-coincident construction lines into one representative each.
 *
 * Two lines belong to the same bucket if their directions are within ~7° of
 * parallel (|sin θ| < 0.122) AND they sit on top of each other geometrically
 * (perpendicular offset < 25% of `lineTolerance`).
 *
 * Within a bucket the representative is the line whose anchor is closest to
 * the optional inception candidate (typically the cursor / drag origin at
 * drag start) — bucket members are coincident, so picking by perpendicular
 * distance to a candidate is degenerate; anchor-to-candidate distance is
 * what disambiguates locally-anchored from far-anchored lines. Without a
 * candidate we fall back to a deterministic source-id pick so the function
 * stays pure and stable across frames.
 *
 * Intended to run ONCE at drag start, not per frame. The construction lines
 * are static during a drag (they only depend on skeleton topology + drag
 * context), so bucketing once gives stable representatives. Per-frame
 * bucketing against the candidate-filtered subset would let lines pop in/out
 * of buckets as they cross the tolerance boundary, which is the bug this
 * function is meant to avoid.
 *
 * `lineTolerance` here should match what the engine will use at resolve time
 * — it sets the offset epsilon for the "same line geometrically" check.
 */
export function bucketConstructionLines(lines: ConstructionLine[], lineTolerance: number, candidate?: { x: number; y: number }): ConstructionLine[] {
  const PARALLEL_SIN_EPS = 0.122;
  const COINCIDENT_OFFSET_FRAC = 0.25;
  const coincidentOffsetEps = lineTolerance * COINCIDENT_OFFSET_FRAC;

  const sourceKey = (line: ConstructionLine): string => line.sourceEdgeId ?? line.sourceNodeId ?? `axis:${line.kind}`;
  // Anchor-to-candidate distance, used for the bucket rep tie-break. Prefer
  // the source's midpoint over the line's origin so coincident lines that
  // share an origin (e.g. extrude-from-edge anchors all par/perp lines at
  // the split point) still pick the rep whose underlying source is closest
  // to the inception. Falls back to origin when no midpoint was supplied.
  const anchorDistSq = (line: ConstructionLine): number => {
    if (!candidate) return 0;
    const ref = line.sourceMidpoint ?? line.origin;
    const dx = ref.x - candidate.x;
    const dy = ref.y - candidate.y;
    return dx * dx + dy * dy;
  };
  const repBetter = (cand: ConstructionLine, rep: ConstructionLine): boolean => {
    if (candidate) {
      const cd = anchorDistSq(cand);
      const rd = anchorDistSq(rep);
      if (cd !== rd) return cd < rd;
    }
    return sourceKey(cand) < sourceKey(rep);
  };

  const reps: ConstructionLine[] = [];
  for (const cand of lines) {
    let placed = false;
    for (let i = 0; i < reps.length; i++) {
      const rep = reps[i];
      const ax = cand.direction.x;
      const ay = cand.direction.y;
      const bx = rep.direction.x;
      const by = rep.direction.y;
      const sinAngle = Math.abs(ax * by - ay * bx);
      if (sinAngle >= PARALLEL_SIN_EPS) continue;
      // Perpendicular offset between the two lines along the shared normal.
      const dx = rep.origin.x - cand.origin.x;
      const dy = rep.origin.y - cand.origin.y;
      const offset = Math.abs(dx * -ay + dy * ax);
      if (offset >= coincidentOffsetEps) continue;
      if (repBetter(cand, rep)) reps[i] = cand;
      placed = true;
      break;
    }
    if (!placed) reps.push(cand);
  }
  return reps;
}

/**
 * Intersection of two infinite lines. Returns null when (near-)parallel.
 * Lines defined as `origin + t * direction`.
 */
function lineLineIntersection(a: ConstructionLine, b: ConstructionLine): THREE.Vector2 | null {
  const denom = a.direction.x * b.direction.y - a.direction.y * b.direction.x;
  if (Math.abs(denom) < 1e-6) return null; // parallel
  const ox = b.origin.x - a.origin.x;
  const oy = b.origin.y - a.origin.y;
  const t = (ox * b.direction.y - oy * b.direction.x) / denom;
  return new THREE.Vector2(a.origin.x + a.direction.x * t, a.origin.y + a.direction.y * t);
}

/**
 * Intersection of an infinite construction line with a finite line segment.
 * Returns the hit point if it falls within [segStart, segEnd] inclusive
 * (with a small epsilon at the ends), else null. Also returns null when the
 * line and segment are (near-)parallel.
 */
function lineSegmentIntersection(line: ConstructionLine, segStart: { x: number; y: number }, segEnd: { x: number; y: number }): THREE.Vector2 | null {
  const sx = segEnd.x - segStart.x;
  const sy = segEnd.y - segStart.y;
  const denom = line.direction.x * sy - line.direction.y * sx;
  if (Math.abs(denom) < 1e-6) return null;
  const ox = segStart.x - line.origin.x;
  const oy = segStart.y - line.origin.y;
  // Solve: line.origin + t * line.direction = segStart + u * (segEnd - segStart)
  // We only need u (segment parameter) to clamp; t can be any real.
  const u = (ox * line.direction.y - oy * line.direction.x) / denom;
  const eps = 1e-4;
  if (u < -eps || u > 1 + eps) return null;
  return new THREE.Vector2(segStart.x + sx * u, segStart.y + sy * u);
}

/**
 * Project `p` onto segment [a, b], clamped to the segment endpoints.
 * Used for both edge-snap and edge-hover preview node placement.
 */
export function closestPointOnSegment(p: THREE.Vector2, a: { x: number; y: number }, b: { x: number; y: number }): THREE.Vector2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-12) return new THREE.Vector2(a.x, a.y);
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2));
  return new THREE.Vector2(a.x + abx * t, a.y + aby * t);
}

/**
 * Project `p` onto the infinite line through `a` and `b` — same math as
 * {@link closestPointOnSegment} but without the [0,1] clamp on `t`. Used
 * for measurements where the segment endpoints are not the meaningful
 * boundary (e.g. the manual width tool, where the wall is conceptually
 * infinite even though we only have a finite skeleton edge to anchor on).
 */
export function closestPointOnInfiniteLine(p: THREE.Vector2, a: { x: number; y: number }, b: { x: number; y: number }): THREE.Vector2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-12) return new THREE.Vector2(a.x, a.y);
  const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  return new THREE.Vector2(a.x + abx * t, a.y + aby * t);
}

/**
 * Helper for callers: turn a screen mouse event into a world-space point on
 * the skeleton's floor plane. Returns null when the camera doesn't intersect.
 */
export function projectMouseToFloor(event: MouseEvent, domElement: HTMLElement, camera: THREE.Camera, floorHeight: number): THREE.Vector3 | null {
  const rect = domElement.getBoundingClientRect();
  const ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  // Unproject two NDC points (one at near plane, one at far plane) to define
  // a line through space at the cursor. Then intersect that line with the
  // horizontal floor plane z=floorHeight.
  //
  // This avoids `Ray.intersectPlane`, which silently returns null for ortho
  // cameras at certain zoom levels — Potree's ortho near=-far convention
  // produces a ray whose t parameter sits outside what intersectPlane
  // accepts. Vector3.unproject is the same machinery used by the working
  // node hover path, so we know it's reliable for this camera.
  const a = new THREE.Vector3(ndcX, ndcY, -1).unproject(camera);
  const b = new THREE.Vector3(ndcX, ndcY, 1).unproject(camera);

  // Parametric line: P(t) = a + t * (b - a). Solve for P(t).z = floorHeight.
  const dz = b.z - a.z;
  if (Math.abs(dz) < 1e-9) {
    // Line is parallel to floor plane (camera looking straight along the floor).
    // Returning null is correct — there's no single hit point.
    return null;
  }
  const t = (floorHeight - a.z) / dz;
  return new THREE.Vector3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, floorHeight);
}

/** A point snapped onto a wall edge: which edge + along-wall offset from its start node. */
export interface WallEdgeHit {
  edgeId: string;
  offset: number;
  /** World position of the snapped point (on the wall centerline). */
  point: THREE.Vector3;
}

/** Snap-reach options for {@link snapToWallEdge}. */
export interface WallSnapOptions {
  edgeRadiusPx?: number;
  nodeSnapRadiusPx?: number;
  /** Extra hit slack past the wall FACES (perpendicular only) — lets callers
   *  snap well before the cursor is over the wall body. */
  thicknessPadPx?: number;
}

/**
 * Generous snap reach for sketch-object placement: objects should glue to a
 * wall well before the cursor reaches the wall body, so the pad pushes the
 * hit band far out past the wall faces.
 */
export const SKETCH_OBJECT_WALL_SNAP: WallSnapOptions = {
  edgeRadiusPx: 24,
  nodeSnapRadiusPx: 16,
  thicknessPadPx: 36,
};

/**
 * Snap the cursor to a wall sketch: prefer a nearby endpoint node, else the
 * nearest point on the nearest edge centerline. Returns the edge + along-wall
 * offset + world point, or null when not near any wall. Shared by the
 * opening-sketch draw tool and the sketch-object WALL constraint.
 */
export function snapToWallEdge(
  event: MouseEvent,
  skeleton: PointoramaWallSketch,
  hitTester: PixelHitTester,
  camera: THREE.Camera,
  domElement: HTMLElement,
  opts: WallSnapOptions = {},
): WallEdgeHit | null {
  const edgeRadiusPx = opts.edgeRadiusPx ?? 14;
  const nodeSnapRadiusPx = opts.nodeSnapRadiusPx ?? 16;

  const mousePx = hitTester.mousePx(event);
  // Openings live on WALLS — the merged outline is a zero-thickness boundary.
  const edgePick = skeleton.pickEdgeAtPixel(mousePx, hitTester, { centerlineRadiusPx: edgeRadiusPx, thicknessPadPx: opts.thicknessPadPx ?? 0, excludeOutline: true });
  if (!edgePick) return null;
  const edge = skeleton.getEdge(edgePick.edgeId);
  if (!edge) return null;
  const a = skeleton.getNode(edge.startNodeId);
  const b = skeleton.getNode(edge.endNodeId);
  if (!a || !b) return null;

  const z = skeleton.getFloorHeight();
  const aXY = { x: a.position.x, y: a.position.y };
  const bXY = { x: b.position.x, y: b.position.y };
  if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
  // Offsets are measured ALONG the edge — arc length for curved edges, so
  // openings and wall runs keep their physical size on a curved wall.
  const len = arcEdgeLength(aXY, bXY, edge.bulge);

  // Snap to an endpoint node when the cursor is within the node radius (px).
  for (const node of [a, b]) {
    const px = hitTester.projectWorldToPixel(node.position);
    if (px && px.distanceTo(mousePx) <= nodeSnapRadiusPx) {
      const offset = node === a ? 0 : len;
      return { edgeId: edge.id, offset, point: new THREE.Vector3(node.position.x, node.position.y, z) };
    }
  }

  // Otherwise project the cursor's floor point onto the edge centerline.
  const floor = projectMouseToFloor(event, domElement, camera, z);
  if (!floor) return null;
  const foot = closestPointOnEdge({ x: floor.x, y: floor.y }, aXY, bXY, edge.bulge);
  const offset = Math.max(0, Math.min(len, foot.s));
  return { edgeId: edge.id, offset, point: new THREE.Vector3(foot.point.x, foot.point.y, z) };
}

// SkeletonEdgeData re-export used in tests / callers — keeps the snap engine
// the single import for skeleton geometry helpers.
export type { SkeletonEdgeData };
